// Replace the existing DashBoardTable component with this updated version
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  LinearProgress,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { endpointMapping } from '../helpers/endpointMapping';

const useStyles = makeStyles({
  container: {
    margin: '15px'
  },
  paper: {
    borderRadius: '8px',
    overflow: 'hidden'
  },
  titleBar: {
    padding: '8px',
    backgroundColor: '#d1f3c5',
    color: '#395032',
    textAlign: 'center',
    fontSize: 'large'
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 220px)',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '-ms-overflow-style': 'none',
    'scrollbarWidth': 'none',
    'overflow': 'auto',
  },
  tableHeader: {
    backgroundColor: '#f3ddc5',
    color: '#395032',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: '12px',
    whiteSpace: 'nowrap',
    borderRight: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
    minWidth: '100px',
  },
  tableDataCell: {
    padding: '12px',
    textAlign: 'center',
    transition: 'background-color 0.3s',
    borderRight: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
  },
  tableRow: {
    '&:nth-of-type(odd)': { 
      backgroundColor: '#f5f5f5' 
    },
    '&:hover': { 
      backgroundColor: '#e8f4ff' 
    },
    transition: 'background-color 0.3s',
  },
  scrollButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
    },
    '& .MuiSvgIcon-root': {
      color: 'black',
    },
    zIndex: 4,
  }
});

const DashBoardTable = ({ date, endpoint, frozenColumns, tableData }) => {
  const classes = useStyles();
  const [internalTableData, setInternalTableData] = useState(tableData || null);
  const [isLoading, setIsLoading] = useState(!tableData);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (tableData) {
      setInternalTableData(tableData);
      setIsLoading(false);
    } else if (date && endpoint) {
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(`/api/${endpointMapping[endpoint]?.firstLevel || ''}?date=${date}`);
          setInternalTableData(response.data);
        } catch (error) {
          console.error('Error fetching table data:', error);
        }
        setIsLoading(false);
      };

      fetchData();
    }
  }, [date, endpoint, tableData]);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 600;
      const currentScroll = tableContainerRef.current.scrollLeft;
      tableContainerRef.current.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getFrozenColumnStyle = (index) => ({
    position: 'sticky',
    left: `${index * 100}px`,
    zIndex: 1,
    backgroundColor: '#f3ddc5',
    fontWeight: 'bold',
    color: '#395032',
  });

  return (
    <Box className={classes.container}>
      <Paper elevation={3} className={classes.paper}>
        <Typography className={classes.titleBar}>
          {internalTableData?.title || ''}
        </Typography>
        {isLoading ? (
          <LinearProgress />
        ) : (
          <Box style={{ position: 'relative' }}>
            <TableContainer
              className={classes.tableContainer}
              ref={tableContainerRef}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {internalTableData?.headers.map((header, index) => (
                      <TableCell 
                        key={index} 
                        className={classes.tableHeader}
                        style={{
                          ...(index < (frozenColumns || 0) ? getFrozenColumnStyle(index) : {}),
                          ...(index < (frozenColumns || 0) ? {zIndex: 3} : {})
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {internalTableData?.data.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      className={classes.tableRow}
                    >
                      {row.map((cell, cellIndex) => (
                        <TableCell 
                          key={cellIndex} 
                          className={classes.tableDataCell}
                          style={{
                            ...(cellIndex < (frozenColumns || 0) ? getFrozenColumnStyle(cellIndex) : {}),
                          }}
                        >
                          {cell !== null && cell !== undefined ? cell : ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
      <Box style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
      }}>
        <IconButton
          onClick={() => scrollTable('left')}
          className={classes.scrollButton}
          style={{
            left: 20,
            pointerEvents: 'auto',
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={() => scrollTable('right')}
          className={classes.scrollButton}
          style={{
            right: 20,
            pointerEvents: 'auto',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default DashBoardTable;